import { useEffect, useRef } from "react";
import * as THREE from "three";
import GUI from "lil-gui";

interface FractalShaderProps {
  showGui?: boolean;
}

export default function FractalShader({ showGui = false }: FractalShaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = document.createElement("canvas");
    canvas.style.display = "block";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    container.appendChild(canvas);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    renderer.setPixelRatio(0.7);

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const geometry = new THREE.PlaneGeometry(2, 2);

    const vertexShader = `
      in vec3 position;
      void main() {
        gl_Position = vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp float;

      uniform vec3 iResolution;
      uniform float iTime;

      uniform int u_renderPasses;
      uniform float u_sceneTimeScale;
      uniform float u_grainIntensity;
      uniform float u_globalLuminance;
      uniform float u_stepPrecision;
      uniform float u_rayIterations;
      uniform float u_surfaceSolidity;

      uniform float u_camPosX;
      uniform float u_camPosY;
      uniform float u_camPitch;
      uniform float u_camFov;
      uniform float u_camShiftX;
      uniform float u_camShiftY;

      out vec4 outColor;

      mat2 calcRotation(float theta) {
        float sine = sin(theta);
        float cosine = cos(theta);
        return mat2(cosine, -sine, sine, cosine);
      }

      vec3 applyCinematicGrade(vec3 rawColor) {
        mat3 colorSpaceA = mat3(
          0.59719, 0.07600, 0.02840,
          0.35458, 0.90834, 0.13383,
          0.04823, 0.01566, 0.83777
        );
        mat3 colorSpaceB = mat3(
          1.60475, -0.10208, -0.00327,
          -0.53108, 1.10813, -0.07276,
          -0.07367, -0.00605, 1.07602
        );

        vec3 graded = colorSpaceA * rawColor;
        vec3 numerator = graded * (graded + 0.0945786) - 0.000090537;
        vec3 denominator = graded * (0.783729 * graded + 0.4329510) + 0.238081;

        return colorSpaceB * (numerator / denominator);
      }

      float computeStructuralDensity(vec3 pos) {
        const float phaseShift = 0.228033988;
        const mat3 structuralBasis = mat3(
          0.388535087, 0.054921382, -0.743402928,
          0.441955127, 4.336973341, 0.258518454,
          0.272087367, 0.174042493, -0.021246185
        );
        return dot(cos(structuralBasis * pos), sin(phaseShift * pos * structuralBasis));
      }

      float getFilmGrain(vec3 seed3D) {
        seed3D = fract(seed3D * 0.1031);
        seed3D += dot(seed3D, seed3D.zyx + 31.32);
        return fract((seed3D.x + seed3D.y) * seed3D.z);
      }

      void mainImage(out vec4 fragColor, in vec2 fragCoord) {
        vec3 finalImage = vec3(0.0);
        float globalTime = iTime * u_sceneTimeScale;

        for(int passX = 0; passX < 4; passX++) {
          if (passX >= u_renderPasses) break;

          for(int passY = 0; passY < 4; passY++) {
            if (passY >= u_renderPasses) break;

            vec2 pixelOffset = (vec2(float(passX), float(passY)) + 0.5) / float(u_renderPasses) - 0.5;
            vec2 uv = fragCoord + pixelOffset;

            vec3 rayOrigin = vec3(u_camPosX, u_camPosY, globalTime);
            vec3 lightAccumulator = vec3(0.0);

            vec3 viewDir = normalize(vec3(u_camFov * uv - iResolution.xy * vec2(u_camShiftX, u_camShiftY), iResolution.y));
            viewDir.yz = calcRotation(u_camPitch) * viewDir.yz;

            float stepDistance;

            for(float iter = 0.0; iter < 100.0; iter += 1.0) {
              if (iter >= u_rayIterations) break;

              vec3 samplePos = rayOrigin;

              float detailNoise = computeStructuralDensity(samplePos * 20.0) / 20.0;
              float baseNoise = computeStructuralDensity(samplePos);

              stepDistance = 0.005 + abs(detailNoise - baseNoise) * 0.7;

              float heightMod = sin(samplePos.z * 2.0 + abs(samplePos.x) * 0.5) * 0.5;
              stepDistance += abs(rayOrigin.y + heightMod) * 0.4;

              float safeStep = stepDistance * u_stepPrecision;
              rayOrigin += viewDir * safeStep;

              float colorPhase = (iter * u_stepPrecision) - 0.4;
              float wavePhase = colorPhase + length(rayOrigin.xz * 0.1) + 2.0;
              vec3 spectrumShift = vec3(3.0, 1.5, 0.5);
              vec3 spectralGlow = 1.0 + 1.5 * sin(wavePhase + spectrumShift);

              float rawDensity = 1.0 / stepDistance;
              float sharpDensity = pow(rawDensity, u_surfaceSolidity) * 0.15;

              lightAccumulator += (spectralGlow * sharpDensity) * u_stepPrecision;
            }

            lightAccumulator *= u_globalLuminance;
            finalImage += applyCinematicGrade(lightAccumulator * lightAccumulator / 1000.0);
          }
        }

        finalImage *= (4.0 / float(u_renderPasses * u_renderPasses));

        finalImage = (finalImage - 0.5) * 0.5 + 0.5;
        finalImage *= 0.3;

        float grainValue = getFilmGrain(vec3(fragCoord, iTime));
        finalImage += (grainValue - 0.5) * u_grainIntensity;

        fragColor = vec4(finalImage, 1.0);
      }

      void main() {
        mainImage(outColor, gl_FragCoord.xy);
      }
    `;

    const params = {
      dpr: 0.7,
      passes: 2,
      speed: 1.0,
      grain: 0.04,
      luminance: 5.1,
      precision: 0.5,
      iterations: 28.6,
      solidity: 1.1,
      camX: 2.8,
      camY: -1.0,
      camPitch: -0.38,
      camFov: 2.5,
      camShiftX: 1.0,
      camShiftY: 1.0,
    };

    const material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader,
      fragmentShader,
      uniforms: {
        iResolution: { value: new THREE.Vector3() },
        iTime: { value: 0.0 },
        u_renderPasses: { value: params.passes },
        u_sceneTimeScale: { value: params.speed },
        u_grainIntensity: { value: params.grain },
        u_globalLuminance: { value: params.luminance },
        u_stepPrecision: { value: params.precision },
        u_rayIterations: { value: params.iterations },
        u_surfaceSolidity: { value: params.solidity },
        u_camPosX: { value: params.camX },
        u_camPosY: { value: params.camY },
        u_camPitch: { value: params.camPitch },
        u_camFov: { value: params.camFov },
        u_camShiftX: { value: params.camShiftX },
        u_camShiftY: { value: params.camShiftY },
      },
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width, height, false);
      const dpr = renderer.getPixelRatio();
      material.uniforms.iResolution.value.set(width * dpr, height * dpr, 1.0);
    };

    const updateDPR = () => {
      renderer.setPixelRatio(params.dpr);
      resize();
    };

    const gui = new GUI({ title: "Scene Settings" });
    if (!showGui) {
      gui.hide();
    }

    gui
      .add(params, "dpr", [0.5, 0.7, 1, 2])
      .name("DPR (Pixel Ratio)")
      .onChange(updateDPR);

    const folderVisuals = gui.addFolder("Light & Visuals");
    folderVisuals
      .add(params, "luminance", 0.1, 15.0)
      .name("Global Luminance")
      .onChange((v: number) => (material.uniforms.u_globalLuminance.value = v));
    folderVisuals
      .add(params, "grain", 0.0, 0.2)
      .name("Cinematic Grain")
      .onChange((v: number) => (material.uniforms.u_grainIntensity.value = v));

    const folderFractal = gui.addFolder("Fractal Engine");
    folderFractal
      .add(params, "solidity", 0.5, 3.0)
      .name("Surface Solidity")
      .onChange((v: number) => (material.uniforms.u_surfaceSolidity.value = v));
    folderFractal
      .add(params, "precision", 0.1, 1.0)
      .name("Step Precision")
      .onChange((v: number) => (material.uniforms.u_stepPrecision.value = v));
    folderFractal
      .add(params, "iterations", 10, 80)
      .name("Ray Iterations")
      .onChange((v: number) => (material.uniforms.u_rayIterations.value = v));

    const folderRender = gui.addFolder("Performance & Time");
    folderRender
      .add(params, "passes", 1, 4, 1)
      .name("AA Render Passes")
      .onChange((v: number) => (material.uniforms.u_renderPasses.value = v));
    folderRender
      .add(params, "speed", 0.0, 5.0)
      .name("Time Scale")
      .onChange((v: number) => (material.uniforms.u_sceneTimeScale.value = v));

    const folderCamera = gui.addFolder("Camera Controls");
    folderCamera
      .add(params, "camX", -5.0, 10.0)
      .name("Position X")
      .onChange((v: number) => (material.uniforms.u_camPosX.value = v));
    folderCamera
      .add(params, "camY", -5.0, 5.0)
      .name("Position Y")
      .onChange((v: number) => (material.uniforms.u_camPosY.value = v));
    folderCamera
      .add(params, "camPitch", -1.5, 1.5)
      .name("Pitch (Tilt)")
      .onChange((v: number) => (material.uniforms.u_camPitch.value = v));
    folderCamera
      .add(params, "camFov", 0.5, 5.0)
      .name("FOV Factor")
      .onChange((v: number) => (material.uniforms.u_camFov.value = v));
    folderCamera
      .add(params, "camShiftX", -2.0, 5.0)
      .name("Pan X (Center)")
      .onChange((v: number) => (material.uniforms.u_camShiftX.value = v));
    folderCamera
      .add(params, "camShiftY", -2.0, 5.0)
      .name("Pan Y (Center)")
      .onChange((v: number) => (material.uniforms.u_camShiftY.value = v));

    gui.close();

    window.addEventListener("resize", resize);
    resize();

    // --- Dynamic speed: hover + scroll velocity + mouse ---
    const baseSpeed = params.speed;
    const hoverBoost = 2.2;   // extra multiplier while hovering
    const scrollBoostMax = 5.0; // cap for scroll-driven boost
    const scrollSensitivity = 0.02; // px/ms -> speed units
    const mouseBoostMax = 3.0;
    const mouseSensitivity = 0.8; // px/ms -> speed units
    const camPanRange = 0.35;   // how far cursor can push the camera pan

    // baseline camera pan (from GUI params)
    const baseCamShiftX = params.camShiftX;
    const baseCamShiftY = params.camShiftY;

    let isHovering = false;
    let lastScrollY = window.scrollY;
    let lastScrollT = performance.now();
    let scrollBoost = 0;
    let currentSpeed = baseSpeed;

    // normalized cursor position (-1..1), target + smoothed
    let targetMx = 0;
    let targetMy = 0;
    let curMx = 0;
    let curMy = 0;

    // mouse velocity boost
    let lastMouseX = 0;
    let lastMouseY = 0;
    let lastMouseT = performance.now();
    let mouseBoost = 0;

    const onEnter = () => {
      isHovering = true;
    };
    const onLeave = () => {
      isHovering = false;
      // ease cursor influence back to center
      targetMx = 0;
      targetMy = 0;
    };
    const onMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      targetMx = Math.max(-1, Math.min(1, nx));
      targetMy = Math.max(-1, Math.min(1, ny));

      const now = performance.now();
      const dx = e.clientX - lastMouseX;
      const dy = e.clientY - lastMouseY;
      const dt = Math.max(1, now - lastMouseT);
      const velocity = Math.sqrt(dx * dx + dy * dy) / dt; // px/ms
      const impulse = Math.min(mouseBoostMax, velocity / mouseSensitivity);
      mouseBoost = Math.max(mouseBoost, impulse);
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;
      lastMouseT = now;
    };

    // Listen on the parent (the hero section) because sibling overlay/content
    // layers sit on top of the shader canvas and would otherwise swallow the
    // cursor events. Falls back to the container itself if no parent.
    const interactionTarget: HTMLElement =
      (container.parentElement as HTMLElement) || container;
    interactionTarget.addEventListener("mouseenter", onEnter);
    interactionTarget.addEventListener("mouseleave", onLeave);
    interactionTarget.addEventListener("mousemove", onMove);

    const onScroll = () => {
      const now = performance.now();
      const dy = window.scrollY - lastScrollY;
      const dt = Math.max(1, now - lastScrollT);
      // pixels per ms -> boost, clamped
      const velocity = Math.abs(dy) / dt;
      const impulse = Math.min(scrollBoostMax, velocity / scrollSensitivity);
      // take the strongest recent impulse
      scrollBoost = Math.max(scrollBoost, impulse);
      lastScrollY = window.scrollY;
      lastScrollT = now;
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const clock = new THREE.Clock();
    let shaderTime = 0;
    let frameId = 0;

    const animate = () => {
      frameId = requestAnimationFrame(animate);

      const dt = clock.getDelta();

      // decay scroll boost over time
      scrollBoost *= Math.pow(0.001, dt); // exponential decay ~ fades in ~1s
      // decay mouse boost too
      mouseBoost *= Math.pow(0.0005, dt);

      // target speed = base * (hover ? hoverBoost : 1) + boosts
      const target =
        baseSpeed * (isHovering ? hoverBoost : 1.0) + scrollBoost + mouseBoost;

      // smooth toward target (easing)
      const ease = 1 - Math.pow(0.001, dt); // ~fast smoothing
      currentSpeed += (target - currentSpeed) * ease;

      // smooth cursor influence
      const camEase = 1 - Math.pow(0.005, dt);
      curMx += (targetMx - curMx) * camEase;
      curMy += (targetMy - curMy) * camEase;

      // apply cursor to camera pan for a parallax feel
      material.uniforms.u_camShiftX.value = baseCamShiftX + curMx * camPanRange;
      material.uniforms.u_camShiftY.value = baseCamShiftY - curMy * camPanRange;

      shaderTime += dt * currentSpeed;
      material.uniforms.iTime.value = shaderTime;
      material.uniforms.u_sceneTimeScale.value = 1.0; // time already scaled in JS

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
      interactionTarget.removeEventListener("mouseenter", onEnter);
      interactionTarget.removeEventListener("mouseleave", onLeave);
      interactionTarget.removeEventListener("mousemove", onMove);
      gui.destroy();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full overflow-hidden bg-black"
    />
  );
}